import { useEffect, useState } from "react";
import axios from "axios";
import { MapPinned, ArrowUp, ArrowDown, Minus } from "lucide-react";
import { Card } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROWS = [
  { key: "medianHouseholdIncome", label: "Median Household Income", fmt: (v) => `$${Math.round(v / 1000)}K` },
  { key: "medianAge", label: "Median Age", fmt: (v) => v.toFixed(1) },
  { key: "bachelorsOrHigherPct", label: "Bachelor's Degree Or Higher", fmt: (v) => `${v}%` },
  { key: "age65PlusPct", label: "Age 65 And Over", fmt: (v) => `${v}%` },
  { key: "homeownerPct", label: "Homeowners", fmt: (v) => `${v}%` },
  { key: "foreignBornPct", label: "Foreign Born", fmt: (v) => `${v}%` },
];

const Delta = ({ area, national }) => {
  if (area == null || national == null) return null;
  const diff = area - national;
  const rel = national ? Math.abs(diff) / national : 0;
  if (rel < 0.03) return <Minus className="w-3 h-3 text-white/35" aria-label="near national" />;
  return diff > 0
    ? <ArrowUp className="w-3 h-3 text-emerald-400" aria-label="above national" />
    : <ArrowDown className="w-3 h-3 text-rose-400" aria-label="below national" />;
};

export const DistrictSnapshot = ({ legislatorId }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true;
    setD(null);
    axios.get(`${API}/legislators/${legislatorId}/constituency`)
      .then((r) => alive && setD(r.data))
      .catch(() => alive && setD({ available: false }));
    return () => { alive = false; };
  }, [legislatorId]);

  if (d && !d.available) return null;
  return (
    <Card className="p-6 rounded-xl border border-white/10" style={{ background: "var(--whip-surface, #131318)" }} data-testid="district-snapshot">
      <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
        <MapPinned className="w-5 h-5 text-orange-400" /> Constituency Snapshot
      </h2>
      {d === null ? (
        <p className="text-sm text-white/50 animate-pulse" data-testid="district-snapshot-loading">Loading Census data…</p>
      ) : (
        <>
          <p className="text-xs text-white/45 mb-4" data-testid="district-snapshot-geo">{d.area?.name}</p>
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 gap-y-2 items-center text-xs">
            <span />
            <span className="font-mono uppercase tracking-wider text-[10px] text-orange-300/90 text-right">Here</span>
            <span className="font-mono uppercase tracking-wider text-[10px] text-white/40 text-right">U.S.</span>
            <span />
            {ROWS.map((r) => {
              const a = d.area?.[r.key];
              const n = d.national?.[r.key];
              return a == null ? null : (
                <div key={r.key} className="contents" data-testid={`district-row-${r.key}`}>
                  <span className="text-white/70">{r.label}</span>
                  <span className="font-mono tabular-nums font-bold text-white text-right">{r.fmt(a)}</span>
                  <span className="font-mono tabular-nums text-white/45 text-right">{n == null ? ", " : r.fmt(n)}</span>
                  <Delta area={a} national={n} />
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-white/35 mt-4" data-testid="district-snapshot-source">
            Source: U.S. Census Bureau, ACS 2020-2024 five-year estimates. Arrows mark values more than 3% away from the national figure.
          </p>
        </>
      )}
    </Card>
  );
};
